/**
 * Risk Management panel guided tour (Bill 2026-10-07 role walkthroughs).
 * Live surfaces only (no SHOW_UNPROVEN screens); nothing is run or saved for
 * the person. Bump `version` when the steps change.
 */
import type { TourDef } from '@retail-ops/guided-tour';

export const TOURS: ReadonlyArray<TourDef> = [
  {
    id: 'risk-panel',
    version: 2,
    title: 'Risk Management panel',
    summary: 'FraudPoint cut-offs, identity and OFAC checks, risk settings, disclosures, the package generator',
    roles: ['RiskManager', 'SuperAdmin'],
    steps: [
      {
        route: '/',
        title: 'The Risk Management panel',
        body: (
          <>
            <p>Risk Management owns these settings and this content for account opening.</p>
            <p>A change applies to the next application; nothing already decided is re-graded.</p>
          </>
        ),
      },
      {
        target: 'risk-tiles',
        title: 'Your areas',
        body: <p>LexisNexis FraudPoint, Risk settings, Disclosures, the Disclosure package generator, and Change history.</p>,
      },
      {
        target: 'ln-cutoffs',
        route: '/lexisnexis',
        title: 'FraudPoint cut-offs',
        body: <p>These scores decide whether an account opening proceeds, pauses for more documents, or is declined. Changing them asks for a reason, which is kept with the change.</p>,
      },
      {
        target: 'ln-run-check',
        title: 'Run a check yourself',
        body: (
          <>
            <p>Run the same LexisNexis identity check and OFAC screening an officer&rsquo;s application runs, for a CIF or a name.</p>
            <p>The results say which lists were searched and how names were matched. The production environment is live and billed; each run asks for a reason.</p>
          </>
        ),
      },
      {
        target: 'rs-tabs',
        route: '/risk-settings',
        title: 'Risk settings',
        body: <p>Every setting that changes a risk or compliance result - identity screening, compliance gates, signing and disclosures. Each change needs a reason, and <strong>Change log</strong> shows who changed what and why.</p>,
      },
      {
        target: 'disc-tiles',
        route: '/disclosures',
        title: 'Disclosures',
        body: (
          <>
            <p>The disclosure templates, their versions, and who gets and signs each one. Write or edit a version in the visual editor.</p>
            <p>A version goes <strong>Submit for review → Approve → Activate</strong>; only the active version is used in a package. The <strong>Approval queue</strong> lists versions waiting for review.</p>
          </>
        ),
      },
      {
        target: 'pg-builder',
        route: '/package-generator',
        title: 'Try a scenario',
        body: <p>Describe a test applicant - ownership, people, products, branch. Nothing is written to Core Director and no customer is involved.</p>,
      },
      {
        target: 'pg-run',
        title: 'See the package',
        body: <p><strong>Generate package</strong> shows the exact documents the signing step would build, in signing order, with a preview of each.</p>,
      },
      {
        target: 'tile-change-history',
        route: '/',
        title: 'Every change is on record',
        body: <p>Every change to settings, disclosures and configuration - yours and everyone else&rsquo;s - is kept with who, when, before and after, and the reason. <strong>Change history</strong> (in the Admin panel) searches it and can restore a change that was set wrong.</p>,
      },
      {
        target: 'report-problem',
        title: 'Report a problem or suggest a change',
        body: <p>The lifebuoy, bottom-left. A picture of the screen and the recent activity go with your report. Suggestions go to management for review first.</p>,
      },
      {
        target: 'tour-start',
        route: '/',
        title: 'Take it again',
        body: <p><strong>Guided tour</strong> in the top bar runs this walkthrough again.</p>,
      },
    ],
  },
];
